/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
import cx from 'classnames';
import PropTypes from 'prop-types';
import {memo, useEffect, useMemo} from 'react';
import {VideoAlphaChannel} from '~library/atoms/VideoAlphaChannel';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {useOverlay} from './actions/useOverlay';

const MemoizedVideoAlphaChannel = memo(VideoAlphaChannel);

/** !!! If animation is playing on mount unexpectedly,
 *  its likely because you need to set hideOnLoad to true.
 *  When omitted, the src changing from "" to your loaded src
 *  will trigger an animation restart.
 */
export const OverlayAnimation = ({
  eventOptionsDesign,
  fixed = false,
  effectVideo = null,
  revealVideo = null,
  className = null,
  portalToBody,
  // biome-ignore lint/suspicious/noEmptyBlockStatements: allow empty
  onCanPlay = () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: allow empty
  onEnded = () => {},
  autoPlay = false,
  hideOnLoad = false,
}) => {
  const isLargishAndUp = useIsLargishAndUp();
  const isReveal = !!revealVideo;

  const {videoRef, visible, hide, restart} = revealVideo || effectVideo || {};
  let selectedOverlay;

  const fallback = useOverlay(!isLargishAndUp, isReveal)?.selectedOverlay;

  if (
    eventOptionsDesign?.reveal_video_desktop_h265 ||
    eventOptionsDesign?.reveal_video_mobile_h265 ||
    eventOptionsDesign?.overlay_video_desktop_h265 ||
    eventOptionsDesign?.overlay_video_desktop_vp9
  ) {
    const overlayType = isReveal ? 'reveal_video' : 'overlay_video';
    selectedOverlay = {
      srcH265: isLargishAndUp
        ? eventOptionsDesign[`${overlayType}_desktop_h265`]
        : eventOptionsDesign[`${overlayType}_mobile_h265`],
      srcVp9: isLargishAndUp
        ? eventOptionsDesign[`${overlayType}_desktop_vp9`]
        : eventOptionsDesign[`${overlayType}_mobile_vp9`],
    };
  } else {
    selectedOverlay = fallback;
  }

  const videoOptions = useMemo(
    () => ({loop: false, autoPlay, muted: true, playsInline: true, controls: false}),
    [autoPlay]
  );

  const srcVp9Url = selectedOverlay?.srcVp9 || selectedOverlay?.desktop_vp9 || '';
  const srcH265Url = selectedOverlay?.srcH265 || selectedOverlay?.desktop_h265 || '';

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (restart) {
      restart();
    }
  }, [srcVp9Url, srcH265Url, restart]);

  useEffect(() => {
    if (hideOnLoad && hide) {
      hide();
    }
  }, [hideOnLoad, hide]);

  const classNames = cx('overlay-animation', className, {
    'overlay-animation--portal-to-body': portalToBody,
    'overlay-animation--fixed': fixed,
    'overlay-animation--hide': !visible,
  });

  if ((!selectedOverlay && selectedOverlay !== '') || srcVp9Url === '' || srcH265Url === '') {
    return (
      // biome-ignore lint/a11y/useMediaCaption: missing track
      <video className={classNames} poster="/assets/images/transparent-pixel.png">
        <source src={null} type="video/webm" />
        Your browser does not support the video tag.
      </video>
    );
  }

  return (
    <MemoizedVideoAlphaChannel
      srcVp9={srcVp9Url}
      srcH265={srcH265Url}
      className={classNames}
      options={videoOptions}
      onCanPlay={onCanPlay}
      onEnded={() => {
        if (hide) {
          hide();
        }
        onEnded();
      }}
      width="600"
      height="100%"
      portalToBody={portalToBody}
      ref={videoRef}
    />
  );
};

OverlayAnimation.propTypes = {
  eventOptionsDesign: PropTypes.shape({
    overlay_video_desktop_h265: PropTypes.string,
    overlay_video_mobile_h265: PropTypes.string,
    overlay_video_desktop_vp9: PropTypes.string,
    overlay_video_mobile_vp9: PropTypes.string,
  }),
  fixed: PropTypes.bool,
  className: PropTypes.string,
  portalToBody: PropTypes.bool,
};
